import { Checkpoint, Command, Config } from '@langchain/langgraph-sdk';

import * as React from 'react';
import { useMemo } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { cn } from '@/utils/tailwind';

import { useGraphAssistant } from '../../api/assistants';
import { useGraphAssistantsDebugSchema } from '../../api/useGraphSwr';
import { StateContext } from '../../control/state';
import { Graph } from '../../data/misc';
import {
  AssistantModalState,
  useAssistantStore,
} from '../../store/assistants/assistantsStore';
import AssistantsModal from '../site/Assistants/AssistantsModal';
import { GraphBreakpointDropdown } from '../site/GraphBreakpointDropdown';
import { GraphMemorySelect } from '../site/GraphMemoryEditor';
import {
  StudioGraphControls,
  StudioThreadControls,
} from '../site/StudioHeaderControls';
import { GraphDatasetContext } from './dataset/GraphDatasetProvider';
import { GraphDatasetToolbar } from './dataset/GraphDatasetToolbar';
import { GraphView } from './graph/graph-view';
import { GraphInputEditor } from './trace/GraphInputEditor';
import { ContinueRefContext } from './trace/GraphInputEditor.utils';
import { GraphTrace } from './trace/GraphTrace';
import { GraphTraceErrorAlert } from './trace/GraphTraceErrorAlert';

export const GraphDebugger = (props: {
  assistantId: string;
  graph: {
    data: Graph | undefined;
    isLoading: boolean;
    error: unknown | undefined;
  };
}) => {
  const state = React.useContext(StateContext);
  const assistantId = useAssistantStore((state) => state.activeAssistantId);
  const { data: assistant } = useGraphAssistant(assistantId);
  const graphId = assistant?.graph_id;
  const datasetState = React.useContext(GraphDatasetContext);

  const continueRef = React.useRef<
    ({
      checkpoint,
      command,
      config,
    }: {
      checkpoint: Checkpoint | string | undefined;
      command?: Command;
      config?: Config;
    }) => void
  >(() => void 0);

  const graphView = useMemo(() => {
    return (
      <GraphView
        // todo: in theory it is possible to have multiple different graphs with the same id
        // note that this may be problematic for how this graph is memoized
        key={graphId}
        className="left-body"
        graphData={props.graph.data}
        isLoading={props.graph.isLoading}
        error={props.graph.error}
      />
    );
  }, [props.graph.data, props.graph.isLoading, props.graph.error, graphId]);

  return (
    <ContinueRefContext.Provider value={continueRef}>
      <DebuggerLayout
        graph={graphView}
        trace={({ className }) => (
          <>
            <GraphTrace className={className} selectedNodeIds={[]} />
            {state.runState.type == 'error' && !state.updates.length && (
              <GraphTraceErrorAlert />
            )}
            {datasetState.isAddingToDataset && <GraphDatasetToolbar />}
          </>
        )}
        input={<DebuggerInput />}
      />
    </ContinueRefContext.Provider>
  );
};

const DebuggerInput = ({ className }: { className?: string }) => {
  const assistantId = useAssistantStore((state) => state.activeAssistantId);

  const {
    data: schemas,
    isLoading,
    error,
  } = useGraphAssistantsDebugSchema(assistantId ? [assistantId] : undefined);
  const schema = schemas?.[0];

  const assistantModalState = useAssistantStore(
    (state) => state.assistantModalState
  );
  const assistantModalOpen = assistantModalState !== AssistantModalState.CLOSED;

  return (
    <>
      <div
        className={cn(
          className,
          'border-1 m-4 mb-5 overflow-y-auto rounded-xl border border-secondary bg-background shadow-lg no-scrollbar empty:hidden'
        )}
      >
        <GraphInputEditor
          schema={schema}
          isSchemaLoading={isLoading}
          schemaError={error}
        />
      </div>
      {assistantModalOpen && <AssistantsModal />}
    </>
  );
};

function PanelAccessibleHandle() {
  return (
    <PanelResizeHandle className="text-secondary data-[resize-handle-active]:text-brand-green-400">
      <div className="group absolute inset-y-0 -mx-1 flex w-2 justify-center">
        <div className="w-[2px] transition-colors group-hover:bg-current" />
      </div>
    </PanelResizeHandle>
  );
}

const DebuggerLayout = (props: {
  graph: React.ReactNode;
  trace: (props: { className?: string }) => React.ReactNode;
  input: React.ReactNode;
}) => {
  return (
    <PanelGroup
      autoSaveId="graph-layout"
      direction="horizontal"
      className="relative grow overflow-hidden border-t border-secondary"
    >
      <Panel
        id="left"
        order={1}
        className="flex flex-col !overflow-y-auto !overflow-x-hidden bg-[#f9fafc] dark:bg-[#1a1c24]"
        defaultSize={50}
        minSize={33}
      >
        <StudioGraphControls />
        <div className="flex items-center justify-between border-t border-secondary p-4">
          <div className="ml-auto flex items-center gap-2">
            <GraphMemorySelect />
            <GraphBreakpointDropdown />
          </div>
        </div>

        {props.graph}
        {props.input}
      </Panel>

      <PanelAccessibleHandle />

      <Panel
        id="right"
        order={2}
        className="relative flex flex-col !overflow-y-auto !overflow-x-hidden border-l border-secondary"
        defaultSize={50}
        minSize={33}
      >
        <StudioThreadControls />

        {props.trace({ className: 'border-secondary' })}
      </Panel>
    </PanelGroup>
  );
};
